import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, ArrowUpRight, Trophy, Users } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { useContent } from '../context/ContentContext';

function Conclave() {
  const { eventsData } = useContent();
  const c = eventsData.conclave;
  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <div className="rounded-3xl overflow-hidden aspect-[16/11]">
          <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
        </div>
      </Reveal>
      <Reveal delay={120}>
        <div>
          <p className="eyebrow text-[#D01919] mb-3">Flagship Event</p>
          <h2 className="font-display text-4xl md:text-5xl">{c.name}</h2>
          <p className="text-[#666666] mt-2">{c.tagline}</p>
          <div className="flex flex-wrap gap-5 mt-4 text-sm">
            <span className="inline-flex items-center gap-1.5"><Calendar size={15} className="text-[#D01919]" /> {c.date}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin size={15} className="text-[#D01919]" /> {c.location}</span>
          </div>
          <p className="text-[#111111] mt-5 leading-relaxed">{c.description}</p>
          <div className="grid grid-cols-3 gap-4 mt-8">
            {c.stats.map((s) => (
              <div key={s.label} className="border-l-2 border-[#D01919] pl-4">
                <p className="font-display text-4xl">{s.value}</p>
                <p className="text-xs text-[#666666] mt-1">{s.label}</p>
              </div>
            ))}
          </div>
          <Link to="/contact" className="inline-flex items-center gap-1.5 mt-8 bg-[#111111] text-white font-medium px-6 py-3.5 rounded-full hover:bg-[#D01919] transition-colors">
            Register interest <ArrowUpRight size={18} />
          </Link>
        </div>
      </Reveal>
    </div>
  );
}

function Felas() {
  const { eventsData } = useContent();
  const f = eventsData.felas;
  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <Reveal delay={120} className="order-2 lg:order-1">
        <div>
          <p className="eyebrow text-[#D01919] mb-3">The Awards</p>
          <h2 className="font-display text-4xl md:text-5xl">{f.name}</h2>
          <p className="text-[#666666] mt-1">{f.full}</p>
          <p className="text-[#111111] mt-5 leading-relaxed">{f.description}</p>
          <div className="flex flex-wrap gap-5 mt-5 text-sm">
            <span className="inline-flex items-center gap-1.5"><Calendar size={15} className="text-[#D01919]" /> {f.date}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin size={15} className="text-[#D01919]" /> {f.location}</span>
          </div>
          <p className="eyebrow text-[#666666] mt-8 mb-3">Award Categories</p>
          <div className="flex flex-wrap gap-2">
            {f.categories.map((cat) => (
              <span key={cat} className="inline-flex items-center gap-1.5 bg-[#f4f4f4] px-3.5 py-2 rounded-full text-sm"><Trophy size={14} className="text-[#D01919]" /> {cat}</span>
            ))}
          </div>
          <Link to="/contact" className="inline-flex items-center gap-1.5 mt-8 bg-[#111111] text-white font-medium px-6 py-3.5 rounded-full hover:bg-[#D01919] transition-colors">
            Nominate / attend <ArrowUpRight size={18} />
          </Link>
        </div>
      </Reveal>
      <Reveal className="order-1 lg:order-2">
        <div className="rounded-3xl overflow-hidden aspect-[16/11]">
          <img src={f.image} alt={f.name} className="w-full h-full object-cover" />
        </div>
      </Reveal>
    </div>
  );
}

function Partners() {
  const { eventsData } = useContent();
  return (
    <div className="grid md:grid-cols-3 gap-6">
      {eventsData.partners.map((e, i) => (
        <Reveal key={e.title} delay={i * 80}>
          <div className="group rounded-2xl overflow-hidden bg-[#f4f4f4] h-full">
            <div className="aspect-[16/10] overflow-hidden"><img src={e.image} alt={e.title} className="card-img w-full h-full object-cover" /></div>
            <div className="p-6">
              <div className="flex items-center gap-2 text-xs text-[#666666]"><Users size={14} className="text-[#D01919]" /> {e.partner}</div>
              <h3 className="font-display text-2xl mt-2">{e.title}</h3>
              <p className="text-sm text-[#666666] mt-2">{e.date} · {e.location}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

export default function Events() {
  const [tab, setTab] = useState('conclave');
  const tabs = [
    { id: 'conclave', label: 'Annual Business Conclave' },
    { id: 'felas', label: 'FELAS Awards' },
    { id: 'partners', label: 'Partner Events' },
  ];
  return (
    <div className="pt-32 md:pt-40 pb-24">
      <div className="section-pad">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <p className="eyebrow text-[#D01919] mb-4">Events</p>
            <h1 className="font-display text-6xl md:text-8xl leading-none">Where the sector convenes.</h1>
            <p className="text-[#666666] text-lg mt-6 max-w-xl">From our flagship conclave to the FELAS awards and partner dialogues — Enersider brings the energy community together.</p>
          </Reveal>
          <div className="flex flex-wrap gap-2 mt-10 mb-14">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${tab === t.id ? 'bg-[#111111] text-white' : 'bg-[#f4f4f4] text-[#111111] hover:bg-[#e5e5e5]'}`}>{t.label}</button>
            ))}
          </div>
          {tab === 'conclave' && <Conclave />}
          {tab === 'felas' && <Felas />}
          {tab === 'partners' && <Partners />}
        </div>
      </div>
    </div>
  );
}
